type ConsoleMethod = 'log' | 'info' | 'warn' | 'error' | 'debug';

interface BufferedEntry {
  timestamp: string;
  level: ConsoleMethod;
  message: string;
}

const MAX_ENTRIES_DEFAULT = 1000;

class ConsoleRingBuffer {
  private buffer: BufferedEntry[] = [];
  private maxEntries: number;
  private original: Partial<Record<ConsoleMethod, any>> = {};
  private isInstalled = false;

  constructor(maxEntries: number = MAX_ENTRIES_DEFAULT) {
    this.maxEntries = maxEntries;
  }

  install() {
    if (this.isInstalled) return;
    const levels: ConsoleMethod[] = ['log', 'info', 'warn', 'error', 'debug'];
    levels.forEach(level => {
      this.original[level] = console[level];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      console[level] = ((...args: any[]) => {
        try {
          const timestamp = new Date().toISOString();
          const message = args.map(arg => {
            if (typeof arg === 'string') return arg;
            try {
              return JSON.stringify(arg);
            } catch {
              return String(arg);
            }
          }).join(' ');
          this.push({ timestamp, level, message });
        } catch {}
        // call original
        try {
          (this.original[level] as Function)?.apply(console, args);
        } catch {}
      }) as unknown as typeof console[ConsoleMethod];
    });
    this.isInstalled = true;
  }

  uninstall() {
    if (!this.isInstalled) return;
    (['log','info','warn','error','debug'] as ConsoleMethod[]).forEach(level => {
      const orig = this.original[level];
      if (orig) {
        // restore original console method
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (console as any)[level] = orig;
      }
    });
    this.isInstalled = false;
  }

  private push(entry: BufferedEntry) {
    this.buffer.push(entry);
    if (this.buffer.length > this.maxEntries) {
      this.buffer.splice(0, this.buffer.length - this.maxEntries);
    }
  }

  getLastLines(n: number): string[] {
    if (n <= 0) return [];
    const start = Math.max(0, this.buffer.length - n);
    return this.buffer.slice(start).map(e => `${e.timestamp} [${e.level.toUpperCase()}] ${e.message}`);
  }
}

export const consoleRingBuffer = new ConsoleRingBuffer();

export function enableConsoleBuffer() {
  consoleRingBuffer.install();
}

export function disableConsoleBuffer() {
  consoleRingBuffer.uninstall();
}


